"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateCartPrices = exports.getPriceDataKey = void 0;
const types_1 = require("../types");
const price_util_1 = require("./price.util");
const split_amount_util_1 = require("./split-amount.util");
const DEFAULT_UNIT_PRICE = 1;
/**
 * Returns the key for a cart item, by concatenating the product ID, price ID and unit price.
 * @param productId {number} - The product ID
 * @param priceId {number} - The price ID
 * @param unitPrice {number} - The unit price
 * @returns {string} - The cart item key
 */
function getPriceDataKey(productId, priceId, unitPrice) {
    return `${productId}_${priceId}_${unitPrice}`;
}
exports.getPriceDataKey = getPriceDataKey;
/**
 * Updates the prices of products in the cart.
 * @template T extends ICartPricingResult
 * @template T2 extends IUpdateCartPrices
 * @param cartPrices {T} - The cart prices
 * @param updatePrices {T2} - The prices to update
 * @returns {T} - The updated cart prices
 */
function updateCartPrices(cartPrices, updatePrices) {
    let pricesMap;
    const cartPricesClone = structuredClone(cartPrices);
    const products = cartPricesClone.products.products.concat(cartPricesClone.products.addons ?? []);
    for (const product of products) {
        product.originalUnitPrice ?? (product.originalUnitPrice = product.unitPrice);
    }
    switch (updatePrices.type) {
        case types_1.UpdatePricesTypeEnum.INDIVIDUAL:
            pricesMap = updateIndividualPrices(products, updatePrices.products);
            break;
        case types_1.UpdatePricesTypeEnum.CATEGORY:
            pricesMap = updateCategoryPrices(cartPricesClone, updatePrices.categories);
            break;
        case types_1.UpdatePricesTypeEnum.GLOBAL:
            pricesMap = updateCartTotalPrice(products, cartPricesClone.totalPrice, updatePrices.totalPrice);
            break;
    }
    return recalculateCartPrices(cartPricesClone, products, pricesMap);
}
exports.updateCartPrices = updateCartPrices;
/**
 * Updates the prices of individual products in the cart.
 * @param products {IProductPrice[]} - The cart products
 * @param updateProducts {IUpdateProductPrices} - The products to update
 * @throws {Error} - If there are no products or addons to update
 */
function updateIndividualPrices(products, updateProducts) {
    if (!updateProducts.products?.length && !updateProducts.addons?.length) {
        throw new Error('No products or addons to update');
    }
    const productUpdatesMap = createProductsUpdateMap((updateProducts.products ?? []).concat(updateProducts.addons ?? []));
    const pricesMap = new Map();
    for (const product of products) {
        const priceKey = getPriceDataKey(product.productId, product.priceId, product.originalUnitPrice);
        const update = productUpdatesMap.get(priceKey);
        if (update) {
            pricesMap.set(priceKey, {
                unitPrice: update.unitPrice,
                totalPrice: (0, price_util_1.calculateTotalPriceWithTax)(update.unitPrice, product.isTaxInclusive, product.tax, product.quantity),
            });
        }
    }
    return pricesMap;
}
/**
 * Creates a map of product prices to update.
 * @param products {IUpdateProductPrice[]} - The products to update
 * @returns {Map<string, IUpdateProductPrice>} - The map of product prices to update
 */
function createProductsUpdateMap(products) {
    return new Map(products?.map(product => [
        getPriceDataKey(product.productId, product.priceId, product.originalUnitPrice),
        product,
    ]) ?? []);
}
/**
 * Updates the prices of products in the cart by categories.
 * @param cartPrices {CartPricingResultDto} - The cart prices
 * @param updateCategories {IUpdateCategoryPrices} - The categories to update
 * @throws {Error} - If the update data is invalid
 */
function updateCategoryPrices(cartPrices, updateCategories) {
    if (!(0, price_util_1.validatePrice)(updateCategories.products) && !(0, price_util_1.validatePrice)(cartPrices.categories.products)) {
        throw new Error('Invalid update data, must have prices to update');
    }
    if (!(0, price_util_1.validatePrice)(cartPrices.categories.addons) && (0, price_util_1.validatePrice)(updateCategories.addons)) {
        throw new Error('Cart has no addons, cannot update prices');
    }
    let productsMap = new Map();
    if ((0, price_util_1.validatePrice)(updateCategories.products)) {
        productsMap = updateCartTotalPrice(cartPrices.products.products, cartPrices.categories.products, updateCategories.products);
    }
    let addonsMap = new Map();
    if ((0, price_util_1.validatePrice)(updateCategories.addons)) {
        addonsMap = updateCartTotalPrice(cartPrices.products.addons, cartPrices.categories.addons, updateCategories.addons);
    }
    updateCategories.products = (0, price_util_1.calculateTotalPriceByPriceField)(Array.from(productsMap.values()), 0, types_1.PriceFieldEnum.TOTAL_PRICE);
    updateCategories.addons = addonsMap.size
        ? (0, price_util_1.calculateTotalPriceByPriceField)(Array.from(addonsMap.values()), 0, types_1.PriceFieldEnum.TOTAL_PRICE)
        : null;
    return new Map([...productsMap, ...addonsMap]);
}
/**
 * Distributes a given amount across the prices in the provided prices map.
 * The distribution is done by splitting the rounding amount among the prices.
 *
 * @param amount - The total amount to be distributed.
 * @param pricesMap - A map where the key is a string and the value is an object implementing the IPriceUpdate interface.
 */
function splitRemainderAmount(amount, pricesMap) {
    const amounts = Array.from(pricesMap.entries()).map(([key, price]) => ({
        amount: price.totalPrice,
        key,
    }));
    (0, split_amount_util_1.splitRoundingAmount)(amount, amounts, types_1.PriceFieldEnum.AMOUNT);
    for (const amount of amounts) {
        pricesMap.get(amount.key).totalPrice = amount.amount;
    }
}
/**
 * Updates the total price of the cart.
 * @param products {IProductPrice[]} - The products
 * @param currentTotalPrice {number} - The current total price
 * @param totalPrice {number} - The new total price
 * @throws {Error} - If the total price is invalid
 */
function updateCartTotalPrice(products, currentTotalPrice, totalPrice) {
    if (!(0, price_util_1.validatePrice)(totalPrice)) {
        throw new Error('Invalid total price');
    }
    const pricesMap = new Map();
    if (!currentTotalPrice && totalPrice) {
        updateProductPricesWhenTotalPriceIsZero(products);
        currentTotalPrice = (0, price_util_1.calculateTotalPriceByPriceField)(products, 0, types_1.PriceFieldEnum.TOTAL_PRICE);
    }
    const ratio = currentTotalPrice ? totalPrice / currentTotalPrice : 0;
    const totalPriceOfProducts = updatePricesMapWithNewUnitPrice(products, ratio, pricesMap);
    if (!(0, price_util_1.samePrice)(totalPriceOfProducts, totalPrice)) {
        splitRemainderAmount(totalPrice, pricesMap);
    }
    return pricesMap;
}
/**
 * Updates the prices of products when the total price is zero.
 * @param products {IProductPrice[]} - The products
 */
function updateProductPricesWhenTotalPriceIsZero(products) {
    const isAllZero = products.every(product => !product.defaultUnitPrice);
    for (const product of products) {
        product.unitPrice = isAllZero ? DEFAULT_UNIT_PRICE : product.defaultUnitPrice ?? 0;
        product.totalPrice = (0, price_util_1.calculateTotalPriceWithTax)(product.unitPrice, product.isTaxInclusive, product.tax, product.quantity);
    }
}
/**
 * Saves the new unit price to the prices map.
 * @param products {IProductPrice[]} - The products
 * @param ratio {number} - The ratio
 * @param pricesMap {Map<string, IPriceUpdate>} - The map of prices
 * @returns {number} - The total price of the products
 */
function updatePricesMapWithNewUnitPrice(products, ratio, pricesMap) {
    for (const product of products ?? []) {
        const priceKey = getPriceDataKey(product.productId, product.priceId, product.originalUnitPrice);
        product.totalPrice = (0, price_util_1.roundPriceCents)(product.totalPrice * ratio);
        product.unitPrice = (0, price_util_1.calculateUnitPriceFromTotal)(product.totalPrice, product.isTaxInclusive, product.tax, product.quantity);
        pricesMap.set(priceKey, { unitPrice: product.unitPrice, totalPrice: product.totalPrice });
    }
    return (0, price_util_1.calculateTotalPriceByPriceField)(products, 0, types_1.PriceFieldEnum.TOTAL_PRICE);
}
/**
 * Recalculates the prices of products in the cart.
 * @template T extends ICartPricingResult
 * @param cartPrices {T} - The cart prices
 * @param products {IProductPrice[]} - The cart's products
 * @param pricesMap {Map<string, IPriceUpdate>} - The map of prices
 * @returns {T} - The updated cart prices
 */
function recalculateCartPrices(cartPrices, products, pricesMap) {
    const hasAddons = !!cartPrices.products.addons?.length;
    updateProducts(products, pricesMap);
    cartPrices.products.productsSubtotal = (0, price_util_1.calculateTotalPriceByPrices)(cartPrices.products.products.map(p => p.subtotal));
    cartPrices.products.addonsSubtotal = hasAddons ? (0, price_util_1.calculateTotalPriceByPrices)(cartPrices.products.addons.map(p => p.subtotal)) : null;
    cartPrices.categories.products = (0, price_util_1.calculateTotalPriceByPriceField)(cartPrices.products.products, 0, types_1.PriceFieldEnum.TOTAL_PRICE);
    cartPrices.categories.addons = hasAddons
        ? (0, price_util_1.calculateTotalPriceByPriceField)(cartPrices.products.addons, 0, types_1.PriceFieldEnum.TOTAL_PRICE)
        : null;
    cartPrices.subtotal = cartPrices.products.addonsSubtotal ?
        (0, price_util_1.roundPriceCents)(cartPrices.products.productsSubtotal + cartPrices.products.addonsSubtotal) :
        cartPrices.products.productsSubtotal;
    cartPrices.totalPrice = (0, price_util_1.roundPriceCents)(cartPrices.categories.products + (cartPrices.categories.addons ?? 0));
    cartPrices.taxAmount = (0, price_util_1.roundPriceCents)(cartPrices.totalPrice - cartPrices.subtotal);
    return cartPrices;
}
/**
 * Updates the prices of products in the cart.
 * @param products {IProductPrice[]} - The products
 * @param pricesMap {Map<string, IPriceUpdate>} - The map of prices
 */
function updateProducts(products, pricesMap) {
    for (const product of products) {
        const priceKey = getPriceDataKey(product.productId, product.priceId, product.originalUnitPrice);
        const update = pricesMap.get(priceKey);
        if (!update) {
            continue;
        }
        product.totalPrice = update.totalPrice;
        product.unitPrice = update.unitPrice;
        product.subtotal = (0, price_util_1.calculateUnitPriceFromTotal)(product.totalPrice, product.isTaxInclusive, product.tax);
        product.taxAmount = (0, price_util_1.roundPriceCents)(product.totalPrice - product.subtotal);
    }
}
//# sourceMappingURL=cart.util.js.map